import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { formatRelativeOrAbsolute, toMachineDate } from '@shared/format';
import { pageCountOf, useCursorPages } from '@shared/pagination';
import { Pager } from '@shared/ui/pager';
import { caseStateBadgeKind } from '@shared/ui/case-state';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { ListSearch } from '@shared/ui/ListSearch';
import { StateBadge } from '@shared/ui/StateBadge';
import { SurfaceError } from '@shared/ui/SurfaceError';

import { CaseSection, CaseTypeGlyph } from '../_shared/components';
import { caseStateLabel, caseTypeLabel, sharedCopy } from '../_shared/copy';
import type { CaseRow } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { useCaseStates } from '../_shared/queries';
import { caseQueueKey, useCaseQueue } from './queries';

const TABLE_COLUMNS = 4;

/**
 * `/cases` — one queue holding all five case types.
 *
 * **One table, not five, and that is the catalog's own words**: «one queue, one
 * thread model, one resolution state». A tab strip per type or a section per
 * state would draw the opposite of what the row states — and the type is already
 * carried on the row.
 *
 * **Four columns: the case, its owner, its state, its last update.** The reason
 * names the row because a case has no title — a reason from the configured list
 * plus free-text detail and nothing else nameable — so it sits in the row header
 * and a screen reader announces the state cell with the case it belongs to. The
 * owner is a column here and not on the subscriber's queue, because this one is
 * worked by someone who is not the person who raised the case.
 *
 * **The type is a line, not a second badge.** Two adjacent badges make the state
 * — the thing an operator is scanning for — compete with a label they are not.
 *
 * **One queue, and no longer two.** `/reports` was this table with
 * `caseType=CONTENT_REPORT` appended — the same rows, from the same read, under a
 * second URL — and the report detail was this queue's own detail with a decision
 * panel bolted on. The panel moved to the case detail, where the endpoint behind
 * it always was, and the second queue went with the URL it lived at.
 *
 * **A reference lookup, and no filter, tab strip, sort, selection, row menu or
 * count.** The lookup is not a filter strip: a case has no title, so the
 * reference is the only thing on the row an operator can be told over the phone
 * and match exactly, and «find the case we are discussing» had no answer on this
 * surface at all — the queue is keyset-paged, so reaching a known case meant
 * walking pages until it appeared. Every other absence traces to a row that does
 * not exist:
 * the queue runs across all four states rather than choosing among them,
 * selection and sort are notification-inbox rows, nothing on this queue is
 * destructive, and the open figure is already the console entry's.
 *
 * **The pager is not one of those absences.** `GET /admin/cases` pages by keyset
 * and answers `has_next` / `next_cursor`, and reading only the first page left
 * every case behind it unreachable — on a *work* queue that is worse than a
 * truncated list, because a row nobody can reach is a case nobody works. The
 * cursors walked are held here rather than in the URL: an opaque keyset token is
 * the service's to define and not a value this console should mint into a
 * shareable address.
 */
export function CasesPage() {
  /** The cursors walked to reach the page in hand; its length is the page ordinal. */
  // The walk lives in the history entry and its ordinal in the URL, so Back and
  // Forward return to the exact page that was on screen — see the shared hook.
  const { cursor, page, hasPrevious, toPrevious, toNext } = useCursorPages();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const reference = searchParams.get('ref') ?? '';
  const looking = reference.trim().length > 0;

  const query = useCaseQueue(cursor, reference);

  /**
   * Writing the reference and clearing the walk are **one** navigation.
   *
   * The cursor stack lives in the history entry and the reference in the query
   * string, so setting one without the other would ask the service for a
   * position in a queue that is no longer the queue being read. `replace`,
   * because typing is one continuous act rather than a page turn.
   */
  function setReference(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('ref', value);
    else next.delete('ref');
    next.delete('page');
    void navigate({ search: next.toString() }, { replace: true, state: { cursorStack: [] } });
  }
  const caseStates = useCaseStates();
  const queryClient = useQueryClient();

  const rows = query.data?.items ?? [];

  /** The configured vocabulary, or `null` until the read lands. */
  const configuredStates = caseStates.data ? new Set(caseStates.data.map((row) => row.value)) : null;

  return (
    <>
      <Breadcrumb items={[{ label: sharedCopy.crumbs.console, to: '/' }, { label: copy.crumb }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <CaseSection id="cases-heading" heading={copy.section.heading}>
        <p className="mb-4 max-w-measure text-xs text-ink-muted">{copy.section.context}</p>

        <ListSearch
          label={copy.lookup.label}
          placeholder={copy.lookup.placeholder}
          hint={copy.lookup.hint}
          value={reference}
          onChange={setReference}
        />

        {query.isError ? (
          <SurfaceError
            error={query.error}
            catalog={errors}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: caseQueueKey(cursor, reference) })}
          />
        ) : null}

        {query.isPending ? (
          <>
            <p role="status" className="sr-only">
              {copy.states.loading}
            </p>
            <CaseTable>
              <RowSkeleton columns={TABLE_COLUMNS} />
            </CaseTable>
          </>
        ) : null}

        {query.data ? (
          rows.length === 0 ? (
            <EmptyState
              message={
                looking
                  ? copy.states.emptyLookup(reference.trim())
                  : hasPrevious
                    ? copy.states.emptyPage
                    : copy.states.empty
              }
              action={
                looking ? (
                  <button
                    type="button"
                    onClick={() => setReference('')}
                    className="hit-target rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
                  >
                    {copy.states.emptyLookupAction}
                  </button>
                ) : undefined
              }
            />
          ) : (
            <CaseTable>
              {rows.map((row) => (
                <CaseTableRow key={row.id} row={row} configuredStates={configuredStates} />
              ))}
            </CaseTable>
          )
        ) : null}

        {query.data ? (
          <Pager
            label={copy.pager.label}
            page={page}
            pageCount={pageCountOf(query.data.total, query.data.page_size)}
            total={query.data.total}
            shown={rows.length}
            noun={{
              one: copy.pager.shownOne,
              two: copy.pager.shownTwo,
              few: copy.pager.shownFew,
              many: copy.pager.shownMany,
            }}
            hasPrevious={hasPrevious}
            hasNext={Boolean(query.data.has_next)}
            onPrevious={toPrevious}
            onNext={() => {
              const next = query.data?.next_cursor;
              if (!next) return;
              toNext(next);
            }}
          />
        ) : null}
      </CaseSection>
    </>
  );
}

/**
 * One case.
 *
 * The secondary line under the reason carries the type, the subject where the
 * type has one, and the identifier — the three facts that tell an operator what
 * they are about to open without taking columns of their own.
 */
function CaseTableRow({
  row,
  configuredStates,
}: {
  row: CaseRow;
  /** `null` until the vocabulary read lands; a token outside it renders literally. */
  configuredStates: Set<string> | null;
}) {
  const configured = configuredStates === null || configuredStates.has(row.caseState);

  return (
    <tr>
      <DTableRowHeader>
        <Link to={`/cases/${row.id}`} className="text-ink hover:underline">
          {/*
            The defect type configures **no reason list at all** — «free text;
            no fixed reason list» — so its rows carry no reason. The pack names
            such a row by the first line of what the raiser wrote, and the queue
            payload carries no message body to take it from; the identifier is
            what names a case that has nothing else, which is the whole reason a
            case ships one. Reported as a gap rather than left blank.
          */}
          {row.reason ?? row.caseReference}
        </Link>

        <span className="mt-1 flex flex-wrap items-center gap-1 text-xs text-ink-muted">
          <CaseTypeGlyph caseType={row.caseType} />
          {caseTypeLabel(row.caseType)}
          {row.subjectLabel ? (
            <>
              {' · '}
              {sharedCopy.about} «{row.subjectLabel}»
            </>
          ) : null}
          {' · '}
          {/* The identifier is Latin in a mono face, isolated, never re-spelled. */}
          <bdi className="mono">{row.caseReference}</bdi>
        </span>
      </DTableRowHeader>

      {/*
        The person, named once.

        The handle used to sit under the name in a second muted line. On this
        platform a subscriber who has not set a display name is served one that
        **is** their handle — `backend-auth` mints `u<suffix>` for both — so the
        cell drew the same string twice, once plain and once with an `@`. The
        name is what an operator reads and the link is what they follow; the
        handle is neither, and the account record it links to carries it.
      */}
      <td>
        {row.raiser?.id ? (
          <Link to={`/users/${row.raiser.id}`} className="text-ink hover:underline">
            {row.raiser.displayName ?? sharedCopy.ownerUnknown}
          </Link>
        ) : (
          <span className="text-ink">{sharedCopy.ownerUnknown}</span>
        )}
      </td>

      <td>
        {/*
          The badge kind is the design system's, assigned once for the whole
          console. A state the platform does not currently configure still
          renders — neutral, carrying its own token — because the taxonomy is
          closed while the vocabulary is not.
        */}
        <StateBadge kind={configured ? caseStateBadgeKind(row.caseState) : 'neutral'}>
          {configured ? caseStateLabel(row.caseState) : row.caseState}
        </StateBadge>
      </td>

      <td className="whitespace-nowrap">
        <time dateTime={toMachineDate(row.lastActivityAt)}>
          {formatRelativeOrAbsolute(row.lastActivityAt)}
        </time>
      </td>
    </tr>
  );
}

/** The table frame, shared by the skeleton and the data states so neither reflows the other. */
function CaseTable({ children }: { children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.caption} minInlineSize="44rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.columns.case}</th>
            <th scope="col">{copy.columns.owner}</th>
            <th scope="col">{copy.columns.state}</th>
            <th scope="col">{copy.columns.updated}</th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}
